import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Activity, Users, Building2, Wallet, MessageSquare,
  Pause, Play, KeyRound, Settings2, RefreshCw, Search, ExternalLink, X,
  Database, Download, CloudUpload, BookOpen, FileText, Gift, Trash2,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const fmtEUR = (n) => (n ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export default function AdminDashboard() {
  const { isSuperAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#06070a] text-slate-200">
      <SuperAdminBanner />
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <Stats />
        <Health />
        <Agencies />
        <Affiliates />
        <Docs />
        <Backups />
        <AuditLog />
      </div>
    </div>
  );
}

function SuperAdminBanner() {
  return (
    <div className="sticky top-0 z-50 bg-rose-600/95 backdrop-blur-md border-b border-rose-400/50 shadow-lg shadow-rose-900/30">
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between text-white">
        <div className="flex items-center gap-3" data-testid="super-admin-banner">
          <Shield size={18} className="shrink-0" />
          <div>
            <div className="text-sm font-bold tracking-wide">SUPER ADMIN — ChatHub Owner Console</div>
            <div className="text-[11px] opacity-90">Chaque action est journalisée. À manier avec précaution.</div>
          </div>
        </div>
        <Link to="/dashboard" data-testid="exit-super-admin" className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-1.5">
          <ExternalLink size={12} /> Exit to dashboard
        </Link>
      </div>
    </div>
  );
}

function Stats() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let active = true;
    api.get('/super-admin/stats')
      .then(({ data }) => { if (active) setData(data); })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || 'Chargement impossible'); });
    return () => { active = false; };
  }, []);
  if (err) return <ErrorCard msg={err} />;
  if (!data) return <SkeletonRow />;
  const items = [
    { icon: Building2, k: data.agencies.total, l: 'Agences', sub: `${data.agencies.suspended} suspended · ${data.agencies.trialing} trialing · ${data.agencies.active} active`, c: 'from-indigo-500 to-violet-500' },
    { icon: Wallet, k: fmtEUR(data.mrr), l: 'Revenus par mois', sub: Object.entries(data.plan_counts).map(([k, v]) => `${v} ${k}`).join(' · ') || '—', c: 'from-emerald-500 to-teal-500' },
    { icon: Users, k: data.users, l: 'Utilisateurs au total', sub: `${data.telegram_accounts.connected}/${data.telegram_accounts.total} TG accounts connected`, c: 'from-amber-500 to-rose-500' },
    { icon: Activity, k: data.activity_24h.messages.toLocaleString('fr-FR'), l: 'Messages /24h', sub: `${data.activity_24h.sales} sales /24h`, c: 'from-cyan-500 to-blue-500' },
  ];
  return (
    <div>
      <SectionTitle title="Overview" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.l} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-5">
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${it.c} flex items-center justify-center mb-3`}>
              <it.icon size={18} className="text-white" />
            </div>
            <div className="font-display text-3xl font-bold text-white">{it.k}</div>
            <div className="text-sm text-slate-300 font-medium mt-0.5">{it.l}</div>
            <div className="text-[11px] text-slate-500 mt-1">{it.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Health() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/super-admin/health').then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const flags = [
    { l: 'FloodWait events (24h)', v: data.flood_wait_24h, danger: data.flood_wait_24h > 5 },
    { l: 'Comptes à reconnecter', v: data.accounts_needing_login, danger: data.accounts_needing_login > 0 },
    { l: 'Comptes en maintenance', v: data.accounts_in_maintenance, danger: false },
  ];
  return (
    <div>
      <SectionTitle title="System Health" />
      <div className="grid md:grid-cols-3 gap-3">
        {flags.map((f) => (
          <div key={f.l} className={`rounded-2xl border p-4 flex items-center justify-between ${f.danger ? 'border-rose-500/40 bg-rose-500/[0.07]' : 'border-white/10 bg-white/[0.02]'}`}>
            <div>
              <div className="text-xs text-slate-400">{f.l}</div>
              <div className={`font-display text-2xl font-bold mt-0.5 ${f.danger ? 'text-rose-300' : 'text-white'}`}>{f.v}</div>
            </div>
            {f.danger ? <AlertTriangle size={20} className="text-rose-300" /> : <Activity size={20} className="text-slate-500" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Agencies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [drawer, setDrawer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeDemo, setIncludeDemo] = useState(false);

  const reload = async () => {
    setLoading(true);
    const { data } = await api.get('/super-admin/agencies', { params: { include_demo: includeDemo } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/super-admin/agencies', { params: { include_demo: includeDemo } })
      .then(({ data }) => { if (active) { setRows(data); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [includeDemo]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filter !== 'all' && r.subscription_status !== filter) return false;
    if (q && !`${r.name} ${r.owner_email || ''}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, filter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <SectionTitle title="Agencies" inline count={rows.length} />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer" data-testid="include-demo-toggle">
            <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} className="rounded border-white/20 bg-white/5" />
            Show demo/test
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="agencies-search" placeholder="Chercher un nom ou un e-mail" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 pr-3 py-1.5 text-xs rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50 w-64" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="agencies-filter" className="text-xs rounded-lg bg-white/5 border border-white/10 text-white px-2 py-1.5 outline-none">
            <option value="all">Tous les statuts</option>
            <option value="trialing">À l’essai</option>
            <option value="active">Actives</option>
            <option value="suspended">Suspendues</option>
          </select>
          <Button size="sm" variant="ghost" onClick={reload} className="text-slate-300 hover:text-white"><RefreshCw size={14} /></Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#08080A] overflow-hidden">
        <table className="w-full text-sm" data-testid="agencies-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <th className="text-left px-4 py-3">Agence</th>
              <th className="text-left px-4 py-3">Propriétaire</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-right px-4 py-3">Employés</th>
              <th className="text-right px-4 py-3">TG</th>
              <th className="text-right px-4 py-3">Msg/24 h</th>
              <th className="text-right px-4 py-3">Ventes 30 j</th>
              <th className="text-right px-4 py-3 pr-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && <tr><td colSpan="9" className="text-center text-slate-500 py-12">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="9" className="text-center text-slate-500 py-12">Aucune agence ne correspond</td></tr>}
            {filtered.map((a) => (
              <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`agency-row-${a.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => setDrawer(a.id)} className="text-white font-medium hover:text-indigo-300 transition-colors">{a.name}</button>
                  <div className="text-[10px] text-slate-500">{new Date(a.created_at).toLocaleDateString('fr-FR')}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-300">{a.owner_email || '—'}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="border-white/10 text-slate-300">{a.plan}</Badge></td>
                <td className="px-4 py-3"><StatusBadge s={a.subscription_status} /></td>
                <td className="px-4 py-3 text-right text-slate-300">{a.employees}</td>
                <td className="px-4 py-3 text-right text-slate-300">{a.telegram_connected}/{a.telegram_accounts}</td>
                <td className="px-4 py-3 text-right text-slate-400">{a.messages_24h.toLocaleString('fr-FR')}</td>
                <td className="px-4 py-3 text-right text-emerald-300 font-medium">{fmtEUR(a.sales_30d_total)}</td>
                <td className="px-4 py-3 text-right pr-4">
                  <AgencyActions a={a} onChange={reload} onResetPwd={(r) => setDrawer({ resetPwd: r })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {typeof drawer === 'string' && <AgencyDrawer agencyId={drawer} onClose={() => setDrawer(null)} onChange={reload} />}
      {drawer?.resetPwd && <ResetPwdModal data={drawer.resetPwd} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    trialing: { c: 'border-amber-500/40 text-amber-300 bg-amber-500/10', l: 'À l’essai' },
    active: { c: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10', l: 'Active' },
    suspended: { c: 'border-rose-500/40 text-rose-300 bg-rose-500/10', l: 'Suspended' },
    canceled: { c: 'border-slate-500/40 text-slate-400 bg-slate-500/10', l: 'Canceled' },
  };
  const v = map[s] || map.trialing;
  return <Badge variant="outline" className={`${v.c} text-[10px] font-semibold`} data-testid={`status-${s}`}>{v.l}</Badge>;
}

function AgencyActions({ a, onChange, onResetPwd }) {
  const suspended = a.subscription_status === 'suspended';
  const isBeta = a.plan === 'Beta (free)';
  const toggle = async () => {
    if (suspended) {
      await api.post(`/super-admin/agencies/${a.id}/unsuspend`);
      toast.success(`${a.name} unsuspended`);
    } else {
      const reason = window.prompt(`Reason for suspending ${a.name}?`, '');
      if (reason === null) return;
      await api.post(`/super-admin/agencies/${a.id}/suspend`, { reason });
      toast.warning(`${a.name} suspended`);
    }
    onChange();
  };
  const toggleBeta = async () => {
    if (isBeta) {
      if (!window.confirm(`Remove free beta access from ${a.name}? They go back to a normal trial.`)) return;
      await api.post(`/super-admin/agencies/${a.id}/revoke-free`);
      toast.warning(`${a.name} beta access revoked`);
    } else {
      if (!window.confirm(`Grant ${a.name} free unlimited beta access (never billed, never expires)?`)) return;
      await api.post(`/super-admin/agencies/${a.id}/grant-free`);
      toast.success(`${a.name} is now a free beta agency 🎁`);
    }
    onChange();
  };
  const resetPwd = async () => {
    if (!a.owner_user_id) { toast.error('Aucun propriétaire trouvé'); return; }
    const { data } = await api.post(`/super-admin/users/${a.owner_user_id}/reset-password`);
    onResetPwd(data);
  };
  const del = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER définitivement l'agence « ${a.name} » et TOUTES ses données (utilisateurs, conversations, ventes, comptes Telegram…) ?\n\nCette action est IRRÉVERSIBLE.`)) return;
    const typed = window.prompt(`Pour confirmer, tape le nom EXACT de l'agence :\n${a.name}`);
    if (typed === null) return;
    if ((typed || '').trim() !== (a.name || '').trim()) { toast.error('Nom incorrect — suppression annulée'); return; }
    try {
      const { data } = await api.delete(`/super-admin/agencies/${a.id}`);
      toast.success(`Agence « ${data.agency_name || a.name} » supprimée`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };
  return (
    <div className="flex items-center justify-end gap-1">
      <Button size="sm" variant="ghost" onClick={resetPwd} data-testid={`reset-pwd-${a.id}`} className="h-7 px-2 text-slate-400 hover:text-amber-300" title="Issue password reset token">
        <KeyRound size={13} />
      </Button>
      <Button size="sm" variant="ghost" onClick={toggleBeta} data-testid={`toggle-beta-${a.id}`} className={`h-7 px-2 ${isBeta ? 'text-violet-300 hover:text-violet-200' : 'text-slate-400 hover:text-violet-300'}`} title={isBeta ? 'Retirer l’accès bêta gratuit' : 'Grant free beta access (never billed)'}>
        <Gift size={13} />
      </Button>
      <Button size="sm" variant="ghost" onClick={toggle} data-testid={`toggle-suspend-${a.id}`} className={`h-7 px-2 ${suspended ? 'text-emerald-400 hover:text-emerald-300' : 'text-rose-400 hover:text-rose-300'}`} title={suspended ? 'Unsuspend' : 'Suspend'}>
        {suspended ? <Play size={13} /> : <Pause size={13} />}
      </Button>
      <Button size="sm" variant="ghost" onClick={del} data-testid={`delete-agency-${a.id}`} className="h-7 px-2 text-slate-500 hover:text-red-400" title="Supprimer définitivement cette agence">
        <Trash2 size={13} />
      </Button>
    </div>
  );
}

function AgencyDrawer({ agencyId, onClose, onChange }) {
  const [d, setD] = useState(null);
  const reload = async () => {
    const { data } = await api.get(`/super-admin/agencies/${agencyId}`);
    setD(data);
  };
  useEffect(() => {
    let active = true;
    api.get(`/super-admin/agencies/${agencyId}`).then(({ data }) => { if (active) setD(data); }).catch(() => {});
    return () => { active = false; };
  }, [agencyId]);

  const toggleMaintenance = async (acc) => {
    const message = !acc.in_maintenance ? window.prompt('Maintenance message shown to the client?', 'Account temporarily in maintenance. Support team is on it.') : '';
    await api.post(`/super-admin/accounts/${acc.id}/maintenance`, { in_maintenance: !acc.in_maintenance, message: message || '' });
    toast.success(`${acc.name} maintenance ${!acc.in_maintenance ? 'ON' : 'OFF'}`);
    reload();
    onChange();
  };
  const forceReconnect = async (acc) => {
    if (!window.confirm(`Force reconnect ${acc.name}? The user will need to re-validate Telegram.`)) return;
    await api.post(`/super-admin/accounts/${acc.id}/force-reconnect`);
    toast.warning(`${acc.name} marked for reconnection`);
    reload();
  };

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="agency-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#08080A] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 bg-[#08080A]/95 backdrop-blur-md border-b border-white/5 p-5 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Agence</div>
            <div className="font-display text-xl font-bold text-white">{d?.agency?.name || '…'}</div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        {!d && <div className="p-8 text-slate-500">Chargement…</div>}
        {d && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Field label="Plan" v={d.agency.plan} />
              <Field label="Status" v={d.agency.subscription_status} />
              <Field label="Currency" v={d.agency.currency} />
              <Field label="Referral" v={d.agency.referral_code} />
              <Field label="Created" v={new Date(d.agency.created_at).toLocaleDateString('fr-FR')} />
              <Field label="Renews" v={d.agency.renews_at ? new Date(d.agency.renews_at).toLocaleDateString('fr-FR') : '—'} />
            </div>

            <div>
              <SectionTitle title={`Telegram accounts (${d.telegram_accounts.length})`} inline />
              <div className="space-y-2">
                {d.telegram_accounts.map((acc) => (
                  <div key={acc.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3 flex items-center justify-between" data-testid={`tg-acc-${acc.id}`}>
                    <div>
                      <div className="text-sm font-medium text-white flex items-center gap-2">
                        {acc.name}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${acc.status === 'ready' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{acc.status}</span>
                        {acc.in_maintenance && <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold bg-rose-500/15 text-rose-300">MAINTENANCE</span>}
                      </div>
                      <div className="text-[10px] text-slate-500">{acc.phone} · {acc.boxes} boxes</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleMaintenance(acc)} data-testid={`maint-${acc.id}`} className="h-7 px-2 text-slate-400 hover:text-amber-300" title="Toggle maintenance">
                        <Settings2 size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => forceReconnect(acc)} data-testid={`reconnect-${acc.id}`} className="h-7 px-2 text-slate-400 hover:text-rose-300" title="Force reconnect">
                        <RefreshCw size={13} />
                      </Button>
                    </div>
                  </div>
                ))}
                {d.telegram_accounts.length === 0 && <div className="text-xs text-slate-500 py-2">Aucun compte</div>}
              </div>
            </div>

            <div>
              <SectionTitle title={`Users (${d.users.length})`} inline />
              <div className="grid grid-cols-2 gap-2 text-xs">
                {d.users.map((u) => (
                  <div key={u.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                    <div className="font-medium text-white">{u.name}</div>
                    <div className="text-slate-500 text-[11px]">{u.email}</div>
                    <div className="mt-1"><span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300">{u.role}</span></div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <SectionTitle title={`Recent sales (last ${d.recent_sales.length})`} inline />
              <div className="space-y-1 text-xs max-h-60 overflow-y-auto">
                {d.recent_sales.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-white/5 py-1.5">
                    <span className="text-slate-400">{new Date(s.date).toLocaleDateString('fr-FR')}</span>
                    <span className="text-slate-300">{s.client}</span>
                    <span className="text-emerald-300 font-medium">{fmtEUR(s.amount)}</span>
                  </div>
                ))}
                {d.recent_sales.length === 0 && <div className="text-slate-500 py-2">Aucune vente pour le moment</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResetPwdModal({ data, onClose }) {
  const url = `${window.location.origin}/auth/reset?token=${data.reset_token}`;
  const copy = () => { navigator.clipboard.writeText(url); toast.success('Lien de réinitialisation copié'); };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70" data-testid="reset-modal">
      <div className="bg-[#08080A] border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2"><KeyRound size={18} className="text-amber-400" /> Mot de passe réinitialisé</h3>
        <p className="text-sm text-slate-400 mb-4">Envoie ce lien à usage unique à <span className="text-white font-medium">{data.user_email}</span>. Valid for {data.expires_in_hours}h.</p>
        <div className="bg-black/40 border border-white/5 rounded-lg p-3 text-[11px] text-slate-300 break-all font-mono mb-3">{url}</div>
        <div className="flex gap-2">
          <Button size="sm" onClick={copy} className="bg-indigo-500 hover:bg-indigo-600 text-white">Copier le lien</Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white">Fermer</Button>
        </div>
      </div>
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let active = true;
    api.get('/super-admin/audit-log?limit=50').then(({ data }) => { if (active) setRows(data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  if (rows.length === 0) return null;
  return (
    <div>
      <SectionTitle title="Recent admin actions" inline count={rows.length} />
      <div className="rounded-2xl border border-white/10 bg-[#08080A] divide-y divide-white/5" data-testid="audit-log">
        {rows.map((r) => (
          <div key={r.id || r.at} className="px-4 py-2.5 flex items-center justify-between text-xs">
            <div>
              <span className="font-mono text-slate-300">{r.action}</span>
              <span className="text-slate-500 ml-2">{JSON.stringify(r.meta || {}).slice(0, 80)}</span>
            </div>
            <span className="text-slate-600">{new Date(r.at).toLocaleString('fr-FR')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const DOC_META = {
  'STRIPE_LIVE_GUIDE.md': { title: 'Stripe Live Setup', desc: 'Passer du mode test au mode live pour encaisser', icon: Wallet, color: 'from-emerald-500 to-teal-500' },
  'DROPFANS_INTEGRATION_GUIDE.md': { title: 'DropFans Integration', desc: 'Connecter DropFans + webhook + mapping modèles', icon: ExternalLink, color: 'from-pink-500 to-rose-500' },
  'GO_LIVE_CHECKLIST.md': { title: 'Go-Live Checklist', desc: 'Tous les outils à configurer pour ouvrir aux clients', icon: BookOpen, color: 'from-indigo-500 to-violet-500' },
};

function Docs() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/super-admin/docs').then(({ data }) => setDocs(data.docs || [])).catch(() => setDocs([]));
  }, []);

  const open = async (name) => {
    setActive(name);
    setLoading(true);
    try {
      const { data } = await api.get(`/super-admin/docs/${encodeURIComponent(name)}`);
      setContent(data.content);
    } catch (e) {
      setContent(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const download = (name) => {
    api.get(`/super-admin/docs/${encodeURIComponent(name)}`).then(({ data }) => {
      const blob = new Blob([data.content], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Téléchargement impossible'));
  };

  return (
    <div data-testid="admin-docs-section">
      <SectionTitle title="Documentation Go-Live" subtitle="Guides étape par étape pour configurer chaque intégration en production" />
      <div className="grid md:grid-cols-3 gap-3">
        {docs.map((name) => {
          const meta = DOC_META[name] || { title: name, desc: 'Guide', icon: FileText, color: 'from-slate-500 to-slate-700' };
          const Ic = meta.icon;
          return (
            <div key={name} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-5">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center mb-3`}>
                <Ic size={18} className="text-white" />
              </div>
              <div className="font-display text-lg font-bold text-white mb-1">{meta.title}</div>
              <div className="text-xs text-slate-400 mb-4 leading-relaxed">{meta.desc}</div>
              <div className="flex gap-2">
                <Button size="sm" data-testid={`doc-view-${name}`} onClick={() => open(name)} className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white h-8 text-xs">
                  <BookOpen size={12} className="mr-1.5" /> Lire
                </Button>
                <Button size="sm" data-testid={`doc-download-${name}`} variant="outline" onClick={() => download(name)} className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 h-8 text-xs">
                  <Download size={12} />
                </Button>
              </div>
            </div>
          );
        })}
        {docs.length === 0 && <div className="col-span-3 text-sm text-slate-500 text-center py-8">Aucun document disponible.</div>}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-[#0B0B0E] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="doc-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-indigo-400" />
                <span className="text-white font-semibold text-sm">{DOC_META[active]?.title || active}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => download(active)} className="h-7 text-xs border-white/10 bg-white/5 text-slate-300">
                  <Download size={11} className="mr-1" /> .md
                </Button>
                <button onClick={() => setActive(null)} className="text-slate-400 hover:text-white"><X size={16} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
              {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : (
                <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-300 font-mono" data-testid="doc-content">{content}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function Backups() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get('/super-admin/backups');
      setData(data);
    } catch (e) {
      setData({ enabled: false, error: e?.response?.data?.detail || 'Failed' });
    }
  };

  useEffect(() => {
    let active = true;
    api.get('/super-admin/backups').then(({ data }) => { if (active) setData(data); }).catch((e) => { if (active) setData({ enabled: false, error: e?.response?.data?.detail || 'Failed' }); });
    return () => { active = false; };
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data: meta } = await api.post('/super-admin/backups/create');
      toast.success(`Backup OK — ${(meta.encrypted_bytes / 1024).toFixed(1)} KB across ${meta.keys.length} folders`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'La sauvegarde a échoué');
    } finally {
      setBusy(false);
    }
  };

  const download = async (key) => {
    try {
      const { data } = await api.get(`/super-admin/backups/download?key=${encodeURIComponent(key)}`);
      window.open(data.url, '_blank');
    } catch (e) {
      toast.error('Lien de téléchargement impossible à générer');
    }
  };

  if (!data) return null;
  if (!data.enabled) {
    return (
      <div>
        <SectionTitle title="Backups" inline />
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 text-sm text-amber-200">
          <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle size={16} /> Sauvegardes non configurées</div>
          <div className="text-amber-300/80 text-xs">Renseigne les variables R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY et R2_ENDPOINT sur le backend, puis redémarre. Une fois configurées, une sauvegarde chiffrée de la base part automatiquement chaque jour à 03h00 UTC, et tu peux en lancer une à la main ici.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <SectionTitle title="Backups (Cloudflare R2)" inline count={data.backups?.length || 0} />
        <Button size="sm" onClick={run} disabled={busy} data-testid="backup-run" className="bg-indigo-500 hover:bg-indigo-600 text-white">
          {busy ? <RefreshCw size={14} className="animate-spin" /> : <CloudUpload size={14} className="mr-1" />}
          {busy ? 'Running…' : 'Lancer une sauvegarde'}
        </Button>
      </div>
      {data.error && <div className="text-xs text-rose-300 mb-2">⚠ {data.error}</div>}
      <div className="rounded-2xl border border-white/10 bg-[#08080A] overflow-hidden" data-testid="backups-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
              <th className="text-left px-4 py-3">Key</th>
              <th className="text-right px-4 py-3">Taille</th>
              <th className="text-left px-4 py-3">Modifié le (UTC)</th>
              <th className="text-right px-4 py-3 pr-4">Télécharger</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(data.backups || []).slice(0, 15).map((b) => (
              <tr key={b.key} className="hover:bg-white/[0.02]">
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-300 flex items-center gap-2"><Database size={12} className="text-indigo-400" /> {b.key}</td>
                <td className="px-4 py-2.5 text-right text-slate-400 text-xs">{(b.size / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{new Date(b.modified).toLocaleString('fr-FR')}</td>
                <td className="px-4 py-2.5 text-right pr-4">
                  <Button size="sm" variant="ghost" onClick={() => download(b.key)} data-testid={`backup-dl-${b.key}`} className="h-7 px-2 text-slate-400 hover:text-indigo-300">
                    <Download size={13} />
                  </Button>
                </td>
              </tr>
            ))}
            {(data.backups || []).length === 0 && (
              <tr><td colSpan="4" className="text-center text-slate-500 py-8 text-xs">Aucune sauvegarde pour l’instant — clique sur &quot;Lancer une sauvegarde&quot;</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data.history?.length > 0 && (
        <div className="mt-3 text-[11px] text-slate-500">
          Last {data.history.length} runs: avg compression ×{(data.history.reduce((a, h) => a + (h.compression_ratio || 1), 0) / data.history.length).toFixed(1)} · avg duration {(data.history.reduce((a, h) => a + (h.duration_sec || 0), 0) / data.history.length).toFixed(1)}s
        </div>
      )}
    </div>
  );
}

// Standalone introducer codes: hand a ?ref= link to a business introducer
// who has NO ChatHub account; signups + first-payment commissions are tracked here.
function Affiliates() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get('/super-admin/affiliates'); setRows(data); }
    catch { setRows([]); toast.error('Chargement des apporteurs impossible'); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Nom de l’apporteur'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/super-admin/affiliates', { name: name.trim() });
      navigator.clipboard?.writeText(data.link);
      toast.success(`Code ${data.code} créé — lien copié`);
      setName('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'La création a échoué');
    } finally { setCreating(false); }
  };

  const markPaid = async (a) => {
    if (!window.confirm(`Marquer comme versées toutes les commissions en attente de ${a.name} ?`)) return;
    try { const { data } = await api.post(`/super-admin/affiliates/${a.id}/mark-paid`); toast.success(`${data.marked} commission(s) marquée(s) versée(s)`); reload(); }
    catch { toast.error('Échec'); }
  };

  const copyLink = (a) => { navigator.clipboard?.writeText(a.link); toast.success('Lien copié'); };

  return (
    <section data-testid="admin-affiliates-section">
      <SectionTitle title="Apporteurs d’affaires" count={rows?.length} subtitle="Tous ceux qui t’amènent des clients, avec ou sans compte ChatHub. Ils touchent 15 % de CHAQUE paiement des agences qu’ils amènent, 18 % à partir de 5 clients qui paient, 20 % à partir de 10, tant que ces clients restent abonnés. « Compte » = apporteur qui a son propre accès ChatHub." />
      <div className="flex gap-2 mb-4 max-w-md">
        <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} placeholder="Nom de l’apporteur (ex. Alex K.)"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-rose-500/50" />
        <Button onClick={create} disabled={creating} className="bg-rose-600 hover:bg-rose-700 text-white">{creating ? 'Création…' : '+ Nouveau code'}</Button>
      </div>
      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-white/[0.03] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5">Nom</th><th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Filleuls</th><th className="px-4 py-2.5">À verser</th><th className="px-4 py-2.5">Déjà versé</th><th className="px-4 py-2.5 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-white/5">
            {(rows || []).map(a => (
              <tr key={a.id} className={a.active ? '' : 'opacity-40'}>
                <td className="px-4 py-2.5 text-white">{a.name}{a.source === 'agence' && <span className="ml-2 text-[9px] uppercase tracking-wide text-indigo-300 bg-indigo-500/15 border border-indigo-500/30 rounded-full px-1.5 py-px" title="Apporteur qui a son propre compte ChatHub">compte</span>}{!a.active && <span className="ml-2 text-[10px] text-slate-500">(inactif)</span>}</td>
                <td className="px-4 py-2.5 font-mono text-rose-300">{a.code}</td>
                <td className="px-4 py-2.5">{a.referred_total} <span className="text-slate-500">({a.referred_active} qui paient)</span></td>
                <td className="px-4 py-2.5 text-amber-300">{(a.earned_pending ?? 0).toFixed(2)} €</td>
                <td className="px-4 py-2.5 text-emerald-300">{(a.earned_paid ?? 0).toFixed(2)} €</td>
                <td className="px-4 py-2.5 text-right space-x-2 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 h-7 text-xs" onClick={() => copyLink(a)}>Copier le lien</Button>
                  {a.earned_pending > 0 && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => markPaid(a)}>Marquer payé</Button>}
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Aucun code d’apporteur pour l’instant. Crées-en un et envoie le lien.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionTitle({ title, count, inline, subtitle }) {
  return (
    <div className={`${inline ? 'mb-3' : 'mb-4'}`}>
      <div className="flex items-center gap-2">
        <h2 className="font-display text-xl font-bold text-white">{title}</h2>
        {count !== undefined && <span className="text-xs text-slate-500">({count})</span>}
      </div>
      {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function Field({ label, v }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5">
      <div className="text-[10px] uppercase text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-200 font-medium truncate">{String(v ?? '—')}</div>
    </div>
  );
}

function SkeletonRow() {
  return <div className="grid md:grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-28 rounded-2xl bg-white/[0.02] border border-white/5 animate-pulse" />)}</div>;
}

function ErrorCard({ msg }) {
  return <div className="rounded-2xl border border-rose-500/40 bg-rose-500/[0.07] p-4 text-rose-200 text-sm flex items-center gap-2"><AlertTriangle size={16} /> {msg}</div>;
}
